/**
 * schematic_catalog.ts — the first crafting slate (original items; SWG-
 * inspired STRUCTURE: slots accept resource families/classes or crafted
 * COMPONENTS, stat-weights per output property, components nest into
 * complex items).
 *
 * CLIENT MIRROR of server/src/lib/schematic_catalog.ts (tables byte-equivalent).
 */
import type { ResStat } from './resource_catalog.js';

export interface SchematicSlot {
  name: string;
  /** 'family:<key>' | 'class:<cls>' | 'component:<itemKey>' */
  accepts: string;
  qty: number;                       // resource units OR component count
  /** Stat weights this slot contributes to QUALITY (Σ over slots ≈ 1). */
  weights: Partial<Record<ResStat, number>>;
}

export interface Schematic {
  key: string;
  name: string;
  kind: 'component' | 'item';
  slots: SchematicSlot[];
  /** Named output properties (each derives from the same weighted stats). */
  props: string[];
  complexity: number;                // refinement cost scale
}

export const SCHEMATICS: Schematic[] = [
  // ── components (tier 1: raw → part) ──
  {
    key: 'metal_ingot', name: 'Metal Ingot', kind: 'component', complexity: 1,
    slots: [{ name: 'Ore', accepts: 'family:ferrite_ore', qty: 20, weights: { PUR: 0.6, DEN: 0.4 } }],
    props: ['integrity'],
  },
  {
    key: 'fiber_weave', name: 'Fiber Weave', kind: 'component', complexity: 1,
    slots: [{ name: 'Fiber', accepts: 'family:fiber', qty: 16, weights: { FLX: 0.6, RES: 0.4 } }],
    props: ['give'],
  },
  {
    key: 'power_cell', name: 'Power Cell', kind: 'component', complexity: 2,
    slots: [
      { name: 'Crystal', accepts: 'family:crystal', qty: 8, weights: { RSN: 0.5, PUR: 0.2 } },
      { name: 'Reagent', accepts: 'family:reagent', qty: 10, weights: { VOL: 0.3 } },
    ],
    props: ['charge'],
  },
  {
    key: 'cured_hide', name: 'Cured Hide', kind: 'component', complexity: 1,
    slots: [
      { name: 'Hide', accepts: 'family:hide', qty: 14, weights: { RES: 0.55, FLX: 0.25 } },
      { name: 'Solvent', accepts: 'family:solvent', qty: 6, weights: { PUR: 0.2 } },
    ],
    props: ['toughness'],
  },
  // ── components (tier 2: parts → assemblies) ──
  {
    key: 'alloy_plate', name: 'Alloy Plate', kind: 'component', complexity: 2,
    slots: [
      { name: 'Ingot', accepts: 'component:metal_ingot', qty: 2, weights: { PUR: 0.5, DEN: 0.2 } },
      { name: 'Flux', accepts: 'family:reagent', qty: 8, weights: { VOL: 0.3 } },
    ],
    props: ['integrity'],
  },
  {
    key: 'circuit_lattice', name: 'Circuit Lattice', kind: 'component', complexity: 3,
    slots: [
      { name: 'Noble Ore', accepts: 'family:noble_ore', qty: 12, weights: { PUR: 0.4, LUS: 0.2 } },
      { name: 'Crystal', accepts: 'family:crystal', qty: 6, weights: { RSN: 0.4 } },
    ],
    props: ['fidelity'],
  },
  {
    key: 'reinforced_frame', name: 'Reinforced Frame', kind: 'component', complexity: 3,
    slots: [
      { name: 'Plates', accepts: 'component:alloy_plate', qty: 2, weights: { PUR: 0.4, DEN: 0.3 } },
      { name: 'Timber', accepts: 'family:timber', qty: 12, weights: { FLX: 0.3 } },
    ],
    props: ['integrity'],
  },
  // ── items (consume components + raws — the COMPLEX layer) ──
  {
    key: 'field_tool', name: 'Field Tool', kind: 'item', complexity: 2,
    slots: [
      { name: 'Head', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.4, DEN: 0.2 } },
      { name: 'Grip', accepts: 'family:timber', qty: 6, weights: { FLX: 0.2 } },
      { name: 'Binding', accepts: 'family:hide', qty: 4, weights: { RES: 0.2 } },
    ],
    props: ['yield_bonus', 'durability'],
  },
  {
    key: 'trail_rations', name: 'Trail Rations', kind: 'item', complexity: 1,
    slots: [
      { name: 'Staple', accepts: 'family:grain', qty: 10, weights: { NUT: 0.6, PUR: 0.2 } },
      { name: 'Fill', accepts: 'class:food', qty: 8, weights: { NUT: 0.2 } },
    ],
    props: ['sustenance'],
  },
  {
    key: 'medic_salve', name: 'Medic Salve', kind: 'item', complexity: 2,
    slots: [
      { name: 'Base', accepts: 'family:reagent', qty: 8, weights: { PUR: 0.5 } },
      { name: 'Binder', accepts: 'family:resin', qty: 6, weights: { FLX: 0.2 } },
      { name: 'Extract', accepts: 'family:produce', qty: 6, weights: { NUT: 0.3 } },
    ],
    props: ['potency'],
  },
  {
    key: 'travelers_pack', name: "Traveler's Pack", kind: 'item', complexity: 3,
    slots: [
      { name: 'Shell', accepts: 'component:cured_hide', qty: 2, weights: { RES: 0.4 } },
      { name: 'Lining', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.3 } },
      { name: 'Straps', accepts: 'family:hide', qty: 6, weights: { RES: 0.3 } },
    ],
    props: ['capacity', 'durability'],
  },
  {
    key: 'extraction_rig_core', name: 'Extraction Rig Core', kind: 'item', complexity: 4,
    slots: [
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { RSN: 0.35 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.35 } },
    ],
    props: ['extraction_bonus'],
  },
  {
    key: 'signal_beacon', name: 'Signal Beacon', kind: 'item', complexity: 3,
    slots: [
      { name: 'Core', accepts: 'component:power_cell', qty: 1, weights: { RSN: 0.5 } },
      { name: 'Housing', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Gas', accepts: 'family:gas', qty: 8, weights: { VOL: 0.2 } },
    ],
    props: ['range'],
  },
  // ── fleet (ship components + droids — equip via the station Dock) ──
  {
    key: 'comp_reactor', name: 'Ship Reactor', kind: 'item', complexity: 4,
    slots: [
      { name: 'Power Core', accepts: 'component:power_cell', qty: 2, weights: { RSN: 0.4, PUR: 0.2 } },
      { name: 'Housing', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.2 } },
      { name: 'Coolant', accepts: 'family:solvent', qty: 8, weights: { VOL: 0.2 } },
    ],
    props: ['output'],
  },
  {
    key: 'comp_engine', name: 'Ship Engine', kind: 'item', complexity: 4,
    slots: [
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Injectors', accepts: 'component:power_cell', qty: 1, weights: { RSN: 0.3 } },
      { name: 'Fuel Line', accepts: 'family:resin', qty: 8, weights: { FLX: 0.2, VOL: 0.2 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'comp_capacitor', name: 'Weapon Capacitor', kind: 'item', complexity: 4,
    slots: [
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.5 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Insulation', accepts: 'family:fiber', qty: 8, weights: { FLX: 0.2 } },
    ],
    props: ['charge'],
  },
  {
    key: 'comp_shield', name: 'Shield Emitter', kind: 'item', complexity: 5,
    slots: [
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Crystal', accepts: 'family:crystal', qty: 10, weights: { PUR: 0.3, LUS: 0.1 } },
      { name: 'Weave', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['strength'],
  },
  {
    key: 'comp_armor', name: 'Hull Plating', kind: 'item', complexity: 4,
    slots: [
      { name: 'Plates', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.5, PUR: 0.2 } },
      { name: 'Backing', accepts: 'component:cured_hide', qty: 1, weights: { RES: 0.3 } },
    ],
    props: ['integrity'],
  },
  {
    key: 'comp_weapon', name: 'Ship Cannons', kind: 'item', complexity: 5,
    slots: [
      { name: 'Barrels', accepts: 'component:metal_ingot', qty: 2, weights: { PUR: 0.3, DEN: 0.2 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.2 } },
    ],
    props: ['damage'],
  },
  {
    key: 'comp_missile', name: 'Missile Rack', kind: 'item', complexity: 5,
    slots: [
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Warheads', accepts: 'family:reagent', qty: 12, weights: { VOL: 0.4 } },
      { name: 'Guidance', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.3 } },
    ],
    props: ['payload'],
  },
  {
    key: 'comp_counter', name: 'Countermeasure Pod', kind: 'item', complexity: 4,
    slots: [
      { name: 'Charges', accepts: 'family:reagent', qty: 10, weights: { VOL: 0.3 } },
      { name: 'Foil', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.3, PUR: 0.2 } },
      { name: 'Trigger', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.2 } },
    ],
    props: ['decoys'],
  },
  {
    key: 'comp_droidport', name: 'Droid Port', kind: 'item', complexity: 4,
    slots: [
      { name: 'Cradle', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Interface', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Padding', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.3 } },
    ],
    props: ['bandwidth'],
  },
  {
    key: 'comp_cargo', name: 'Cargo Hold', kind: 'item', complexity: 3,
    slots: [
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Panels', accepts: 'component:alloy_plate', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Netting', accepts: 'family:fiber', qty: 10, weights: { FLX: 0.3 } },
    ],
    props: ['capacity'],
  },
  {
    key: 'droid_aegis', name: 'Aegis Combat Droid', kind: 'item', complexity: 5,
    slots: [
      { name: 'Core', accepts: 'component:circuit_lattice', qty: 2, weights: { RSN: 0.4 } },
      { name: 'Chassis', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { PUR: 0.3 } },
    ],
    props: ['protocols'],
  },
  {
    key: 'droid_ember', name: 'Ember Work Droid', kind: 'item', complexity: 4,
    slots: [
      { name: 'Core', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { PUR: 0.3 } },
    ],
    props: ['protocols'],
  },
  // ── THE DROID CLASSES (2026-09-24): one recipe per class (lib/droid_classes.ts). The supply
  //    hauler is basic work (complexity 3, open to all); the rest need a SCHEMATIC. ──
  {
    key: 'droid_supply', name: 'Supply Droid', kind: 'item', complexity: 3,
    slots: [
      { name: 'Chassis', accepts: 'component:reinforced_frame', qty: 2, weights: { DEN: 0.4 } },
      { name: 'Legs', accepts: 'component:servo_motor', qty: 2, weights: { FLX: 0.3, RES: 0.2 } },
      { name: 'Core', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['protocols', 'capacity'],
  },
  {
    key: 'droid_humanoid', name: 'Humanoid Droid', kind: 'item', complexity: 4,
    slots: [
      { name: 'Shell', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Joints', accepts: 'component:servo_motor', qty: 4, weights: { FLX: 0.3 } },
      { name: 'Brain', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.3 } },
      { name: 'Core', accepts: 'component:circuit_lattice', qty: 2, weights: { RSN: 0.2 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['protocols', 'dexterity'],
  },
  {
    key: 'droid_medic', name: 'Medical Droid', kind: 'item', complexity: 4,
    slots: [
      { name: 'Shell', accepts: 'component:alloy_plate', qty: 1, weights: { PUR: 0.2 } },
      { name: 'Manipulators', accepts: 'component:servo_motor', qty: 2, weights: { FLX: 0.3 } },
      { name: 'Diagnostics', accepts: 'component:circuit_lattice', qty: 2, weights: { RSN: 0.3 } },
      { name: 'Medicine', accepts: 'family:reagent', qty: 12, weights: { VOL: 0.2, PUR: 0.2 } },
      { name: 'Seals', accepts: 'component:sealant_gel', qty: 1, weights: { RES: 0.2 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['protocols', 'care'],
  },
  {
    key: 'droid_battle', name: 'Battle Droid', kind: 'item', complexity: 5,
    slots: [
      { name: 'Armor', accepts: 'component:alloy_plate', qty: 3, weights: { DEN: 0.4, RES: 0.2 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.2 } },
      { name: 'Joints', accepts: 'component:servo_motor', qty: 2, weights: { FLX: 0.2 } },
      { name: 'Targeting', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.3 } },
      { name: 'Barrel', accepts: 'component:blaster_barrel', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 2, weights: { VOL: 0.2 } },
    ],
    props: ['protocols', 'firepower'],
  },
  {
    key: 'droid_ship', name: 'Ship Droid', kind: 'item', complexity: 5,
    slots: [
      { name: 'Shell', accepts: 'component:hull_segment', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Tools', accepts: 'component:servo_motor', qty: 2, weights: { FLX: 0.3 } },
      { name: 'Navigation', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.3 } },
      { name: 'Core', accepts: 'component:circuit_lattice', qty: 2, weights: { RSN: 0.2 } },
      { name: 'Wiring', accepts: 'component:insulated_wire', qty: 2, weights: { PUR: 0.2 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.2 } },
    ],
    props: ['protocols', 'engineering'],
  },
  {
    key: 'droid_mega', name: 'Mega Battle Droid', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 4, weights: { DEN: 0.4 } },
      { name: 'Armor', accepts: 'component:alloy_plate', qty: 6, weights: { DEN: 0.3, RES: 0.3 } },
      { name: 'Treads', accepts: 'component:reinforced_frame', qty: 3, weights: { RES: 0.2 } },
      { name: 'Drive', accepts: 'component:servo_motor', qty: 4, weights: { FLX: 0.2 } },
      { name: 'Targeting', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.3 } },
      { name: 'Cannons', accepts: 'component:blaster_barrel', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Reactor', accepts: 'component:power_cell', qty: 4, weights: { VOL: 0.3 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 2, weights: { PUR: 0.2 } },
    ],
    props: ['protocols', 'firepower', 'armor'],
  },

  // ── GROUND-COMBAT WEAPONS (the cost law: power tracks depth — higher
  // tiers take more components, rarer families, bigger quantities) ──
  // components
  {
    key: 'blaster_barrel', name: 'Blaster Barrel', kind: 'component', complexity: 2,
    slots: [
      { name: 'Ore', accepts: 'family:ferrite_ore', qty: 24, weights: { PUR: 0.5, DEN: 0.3 } },
      { name: 'Gas', accepts: 'family:gas', qty: 10, weights: { VOL: 0.2 } },
    ],
    props: ['bore'],
  },
  {
    key: 'focus_crystal', name: 'Focus Crystal', kind: 'component', complexity: 3,
    slots: [
      { name: 'Crystal', accepts: 'family:crystal', qty: 16, weights: { LUS: 0.6, PUR: 0.2 } },
      { name: 'Solvent', accepts: 'family:solvent', qty: 6, weights: { RSN: 0.2 } },
    ],
    props: ['clarity'],
  },
  {
    key: 'blade_billet', name: 'Blade Billet', kind: 'component', complexity: 2,
    slots: [
      { name: 'Ore', accepts: 'family:ferrite_ore', qty: 28, weights: { DEN: 0.5, PUR: 0.3 } },
      { name: 'Resin', accepts: 'family:resin', qty: 8, weights: { RES: 0.2 } },
    ],
    props: ['edge'],
  },
  {
    key: 'weapon_grip', name: 'Weapon Grip', kind: 'component', complexity: 1,
    slots: [
      { name: 'Hide', accepts: 'family:hide', qty: 10, weights: { FLX: 0.6 } },
      { name: 'Timber', accepts: 'family:timber', qty: 8, weights: { DEN: 0.4 } },
    ],
    props: ['hold'],
  },
  {
    key: 'storm_core', name: 'Storm Core', kind: 'component', complexity: 5,
    slots: [
      { name: 'Noble Ore', accepts: 'family:noble_ore', qty: 18, weights: { PUR: 0.4, LUS: 0.2 } },
      { name: 'Gas', accepts: 'family:gas', qty: 16, weights: { VOL: 0.2 } },
      { name: 'Crystal', accepts: 'family:crystal', qty: 10, weights: { RSN: 0.2 } },
    ],
    props: ['fury'],
  },
  // tier 1 guns
  {
    key: 'scrap_pistol', name: 'Scrap Pistol', kind: 'item', complexity: 2,
    slots: [
      { name: 'Barrel', accepts: 'component:blaster_barrel', qty: 1, weights: { PUR: 0.6 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.4 } },
    ],
    props: ['damage'],
  },
  {
    key: 'bolt_carbine', name: 'Bolt Carbine', kind: 'item', complexity: 2,
    slots: [
      { name: 'Barrel', accepts: 'component:blaster_barrel', qty: 1, weights: { PUR: 0.5 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.2 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.3 } },
    ],
    props: ['damage'],
  },
  // tier 2 guns
  {
    key: 'arc_pistol', name: 'Arc Pistol', kind: 'item', complexity: 3,
    slots: [
      { name: 'Barrel', accepts: 'component:blaster_barrel', qty: 1, weights: { PUR: 0.4 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.4 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['damage'],
  },
  {
    key: 'scatter_gun', name: 'Scatter Gun', kind: 'item', complexity: 3,
    slots: [
      { name: 'Barrels', accepts: 'component:blaster_barrel', qty: 2, weights: { PUR: 0.5 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['damage'],
  },
  // tier 3 guns
  {
    key: 'pulse_rifle', name: 'Pulse Rifle', kind: 'item', complexity: 4,
    slots: [
      { name: 'Barrel', accepts: 'component:blaster_barrel', qty: 1, weights: { PUR: 0.35 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.3 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.2 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.15 } },
    ],
    props: ['damage'],
  },
  {
    key: 'long_rifle', name: 'Long Rifle', kind: 'item', complexity: 4,
    slots: [
      { name: 'Barrels', accepts: 'component:blaster_barrel', qty: 2, weights: { PUR: 0.4 } },
      { name: 'Focus', accepts: 'component:focus_crystal', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.3 } },
    ],
    props: ['damage'],
  },
  // tier 4 guns
  {
    key: 'plasma_repeater', name: 'Plasma Repeater', kind: 'item', complexity: 5,
    slots: [
      { name: 'Barrels', accepts: 'component:blaster_barrel', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 2, weights: { VOL: 0.35 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.2 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.15 } },
    ],
    props: ['damage'],
  },
  {
    key: 'ion_blaster', name: 'Ion Blaster', kind: 'item', complexity: 5,
    slots: [
      { name: 'Barrel', accepts: 'component:blaster_barrel', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 2, weights: { VOL: 0.3 } },
      { name: 'Focus', accepts: 'component:focus_crystal', qty: 1, weights: { LUS: 0.25 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.15 } },
    ],
    props: ['damage'],
  },
  // tier 5 guns — the masterworks (storm_core gates them behind noble ore)
  {
    key: 'storm_cannon', name: 'Storm Cannon', kind: 'item', complexity: 6,
    slots: [
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.3, VOL: 0.15 } },
      { name: 'Barrels', accepts: 'component:blaster_barrel', qty: 2, weights: { DEN: 0.2 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 2, weights: { VOL: 0.15 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.1 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.1 } },
    ],
    props: ['damage'],
  },
  {
    key: 'aurora_lance', name: 'Aurora Lance', kind: 'item', complexity: 6,
    slots: [
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.25 } },
      { name: 'Foci', accepts: 'component:focus_crystal', qty: 2, weights: { LUS: 0.35 } },
      { name: 'Barrel', accepts: 'component:blaster_barrel', qty: 1, weights: { DEN: 0.15 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.15 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.1 } },
    ],
    props: ['damage'],
  },
  // melee (tier ladder)
  {
    key: 'iron_machete', name: 'Iron Machete', kind: 'item', complexity: 2,
    slots: [
      { name: 'Blade', accepts: 'component:blade_billet', qty: 1, weights: { DEN: 0.6 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.4 } },
    ],
    props: ['damage'],
  },
  {
    key: 'vibro_knife', name: 'Vibro Knife', kind: 'item', complexity: 3,
    slots: [
      { name: 'Blade', accepts: 'component:blade_billet', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.35 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.25 } },
    ],
    props: ['damage'],
  },
  {
    key: 'duelist_saber', name: 'Duelist Saber', kind: 'item', complexity: 4,
    slots: [
      { name: 'Blade', accepts: 'component:blade_billet', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Focus', accepts: 'component:focus_crystal', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.15 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['damage'],
  },
  {
    key: 'thunder_maul', name: 'Thunder Maul', kind: 'item', complexity: 5,
    slots: [
      { name: 'Heads', accepts: 'component:blade_billet', qty: 2, weights: { DEN: 0.45 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { PUR: 0.2 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.15 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['damage'],
  },
  {
    key: 'nova_blade', name: 'Nova Blade', kind: 'item', complexity: 6,
    slots: [
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Rails', accepts: 'component:blade_billet', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Focus', accepts: 'component:focus_crystal', qty: 1, weights: { LUS: 0.25 } },
      { name: 'Grip', accepts: 'component:weapon_grip', qty: 1, weights: { FLX: 0.15 } },
    ],
    props: ['damage'],
  },

  // ── FURNISHING WAVE (34 craftable furniture pieces — placeable in owned
  // structures via placeFurniture; versona smiths furnish from the same
  // slate). Modest costs: home comfort, not armament. ──
  // components
  {
    key: 'timber_plank', name: 'Timber Plank', kind: 'component', complexity: 1,
    slots: [
      { name: 'Timber', accepts: 'family:timber', qty: 12, weights: { DEN: 0.5, PUR: 0.2 } },
      { name: 'Resin', accepts: 'family:resin', qty: 4, weights: { FLX: 0.3 } },
    ],
    props: ['grain'],
  },
  {
    key: 'glass_pane', name: 'Glass Pane', kind: 'component', complexity: 1,
    slots: [
      { name: 'Crystal', accepts: 'family:crystal', qty: 12, weights: { PUR: 0.6 } },
      { name: 'Solvent', accepts: 'family:solvent', qty: 4, weights: { LUS: 0.4 } },
    ],
    props: ['clarity'],
  },
  // seating
  {
    key: 'sofa_low', name: 'Low Sofa', kind: 'item', complexity: 2,
    slots: [
      { name: 'Frame', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Cushions', accepts: 'component:fiber_weave', qty: 2, weights: { FLX: 0.5 } },
      { name: 'Fittings', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'armchair_wing', name: 'Wing Armchair', kind: 'item', complexity: 2,
    slots: [
      { name: 'Frame', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Upholstery', accepts: 'component:cured_hide', qty: 1, weights: { RES: 0.4 } },
      { name: 'Padding', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.3 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'chair_dining', name: 'Dining Chair', kind: 'item', complexity: 1,
    slots: [
      { name: 'Shell', accepts: 'component:timber_plank', qty: 1, weights: { DEN: 0.4, FLX: 0.3 } },
      { name: 'Legs', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.3 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'stool_shop', name: 'Shop Stool', kind: 'item', complexity: 1,
    slots: [
      { name: 'Seat', accepts: 'component:cured_hide', qty: 1, weights: { RES: 0.5 } },
      { name: 'Legs', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.5 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'bench_hall', name: 'Hall Bench', kind: 'item', complexity: 1,
    slots: [
      { name: 'Seat', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.6 } },
      { name: 'Supports', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.4 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'pod_lounge', name: 'Lounge Pod', kind: 'item', complexity: 2,
    slots: [
      { name: 'Shell', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Padding', accepts: 'component:fiber_weave', qty: 2, weights: { FLX: 0.45 } },
      { name: 'Base', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['comfort'],
  },
  // tables
  {
    key: 'table_dining', name: 'Dining Table', kind: 'item', complexity: 2,
    slots: [
      { name: 'Top', accepts: 'component:timber_plank', qty: 3, weights: { DEN: 0.5 } },
      { name: 'Trestles', accepts: 'component:metal_ingot', qty: 2, weights: { PUR: 0.5 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'table_low', name: 'Low Table', kind: 'item', complexity: 1,
    slots: [
      { name: 'Top', accepts: 'family:stone', qty: 14, weights: { DEN: 0.55 } },
      { name: 'Banding', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.45 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'desk_writing', name: 'Writing Desk', kind: 'item', complexity: 2,
    slots: [
      { name: 'Body', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.4 } },
      { name: 'Inlay', accepts: 'component:cured_hide', qty: 1, weights: { RES: 0.3 } },
      { name: 'Pulls', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.3 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'table_side', name: 'Side Table', kind: 'item', complexity: 1,
    slots: [
      { name: 'Tray', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.5 } },
      { name: 'Legs', accepts: 'component:timber_plank', qty: 1, weights: { DEN: 0.5 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'counter_bar', name: 'Counter Bar', kind: 'item', complexity: 2,
    slots: [
      { name: 'Worktop', accepts: 'family:stone', qty: 16, weights: { DEN: 0.4 } },
      { name: 'Cabinet', accepts: 'component:timber_plank', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Rail', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.3 } },
    ],
    props: ['sturdiness'],
  },
  // sleep
  {
    key: 'bed_single', name: 'Single Bed', kind: 'item', complexity: 2,
    slots: [
      { name: 'Frame', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Bedding', accepts: 'component:fiber_weave', qty: 2, weights: { FLX: 0.5 } },
      { name: 'Fittings', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'bed_double', name: 'Double Bed', kind: 'item', complexity: 2,
    slots: [
      { name: 'Frame', accepts: 'component:timber_plank', qty: 3, weights: { DEN: 0.3 } },
      { name: 'Bedding', accepts: 'component:fiber_weave', qty: 3, weights: { FLX: 0.45 } },
      { name: 'Inlay', accepts: 'family:crystal', qty: 4, weights: { LUS: 0.25 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'pod_sleep', name: 'Sleep Pod', kind: 'item', complexity: 3,
    slots: [
      { name: 'Shell', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.35 } },
      { name: 'Bedding', accepts: 'component:fiber_weave', qty: 2, weights: { FLX: 0.35 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.3 } },
    ],
    props: ['comfort'],
  },
  // storage
  {
    key: 'wardrobe_tall', name: 'Tall Wardrobe', kind: 'item', complexity: 2,
    slots: [
      { name: 'Body', accepts: 'component:timber_plank', qty: 3, weights: { DEN: 0.55 } },
      { name: 'Hinges', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.45 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'shelf_unit', name: 'Shelf Unit', kind: 'item', complexity: 1,
    slots: [
      { name: 'Shelves', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.5 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.5 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'cabinet_low', name: 'Low Cabinet', kind: 'item', complexity: 1,
    slots: [
      { name: 'Body', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.55 } },
      { name: 'Feet', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.45 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'chest_storage', name: 'Storage Chest', kind: 'item', complexity: 1,
    slots: [
      { name: 'Shell', accepts: 'component:timber_plank', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Banding', accepts: 'component:cured_hide', qty: 1, weights: { RES: 0.35 } },
      { name: 'Clasp', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.25 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'bookcase_arch', name: 'Arched Bookcase', kind: 'item', complexity: 2,
    slots: [
      { name: 'Case', accepts: 'component:timber_plank', qty: 3, weights: { DEN: 0.6 } },
      { name: 'Trim', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.4 } },
    ],
    props: ['sturdiness'],
  },
  // light
  {
    key: 'lamp_floor', name: 'Floor Lamp', kind: 'item', complexity: 1,
    slots: [
      { name: 'Shade', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.3 } },
      { name: 'Tripod', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.4 } },
    ],
    props: ['radiance'],
  },
  {
    key: 'lamp_table', name: 'Table Lamp', kind: 'item', complexity: 1,
    slots: [
      { name: 'Dome', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.4 } },
      { name: 'Stem', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.2 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.4 } },
    ],
    props: ['radiance'],
  },
  {
    key: 'light_column', name: 'Light Column', kind: 'item', complexity: 2,
    slots: [
      { name: 'Rod', accepts: 'family:crystal', qty: 8, weights: { RSN: 0.4, LUS: 0.2 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.2 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.2 } },
    ],
    props: ['radiance'],
  },
  {
    key: 'stove_hearth', name: 'Hearth Stove', kind: 'item', complexity: 2,
    slots: [
      { name: 'Body', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Window', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Flue', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.3 } },
    ],
    props: ['radiance'],
  },
  // decor
  {
    key: 'plant_large', name: 'Broadleaf Planter', kind: 'item', complexity: 1,
    slots: [
      { name: 'Cutting', accepts: 'family:produce', qty: 8, weights: { RSN: 0.5 } },
      { name: 'Planter', accepts: 'family:stone', qty: 8, weights: { DEN: 0.5 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'plant_small', name: 'Succulent Bowl', kind: 'item', complexity: 1,
    slots: [
      { name: 'Cutting', accepts: 'family:produce', qty: 5, weights: { RSN: 0.5 } },
      { name: 'Bowl', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.5 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'rug_woven', name: 'Woven Rug', kind: 'item', complexity: 1,
    slots: [
      { name: 'Weave', accepts: 'component:fiber_weave', qty: 3, weights: { FLX: 0.6, RES: 0.4 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'screen_holo', name: 'Holo Screen', kind: 'item', complexity: 3,
    slots: [
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Pane', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Pedestal', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.3 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'statue_small', name: 'Jade Bird', kind: 'item', complexity: 2,
    slots: [
      { name: 'Stone', accepts: 'family:stone', qty: 8, weights: { LUS: 0.6 } },
      { name: 'Plinth', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.4 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'terrarium_pod', name: 'Terrarium Pod', kind: 'item', complexity: 2,
    slots: [
      { name: 'Sphere', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.4 } },
      { name: 'Moss', accepts: 'family:produce', qty: 6, weights: { RSN: 0.35 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.25 } },
    ],
    props: ['artistry'],
  },
  // utility
  {
    key: 'kitchen_unit', name: 'Galley Kitchen', kind: 'item', complexity: 3,
    slots: [
      { name: 'Worktop', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Cabinets', accepts: 'component:timber_plank', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.35 } },
    ],
    props: ['sturdiness'],
  },
  // ── FUNCTIONAL FURNITURE (2026-09-24) ──
  {
    key: 'crate_storage', name: 'Storage Crate', kind: 'item', complexity: 2,
    slots: [
      { name: 'Shell', accepts: 'component:metal_ingot', qty: 3, weights: { DEN: 0.4 } },
      { name: 'Lid', accepts: 'component:timber_plank', qty: 2, weights: { PUR: 0.2 } },
      { name: 'Latches', accepts: 'component:insulated_wire', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'safe_vault', name: 'Safe Vault', kind: 'item', complexity: 3,
    slots: [
      { name: 'Armour', accepts: 'component:metal_ingot', qty: 4, weights: { DEN: 0.5 } },
      { name: 'Lock', accepts: 'component:servo_motor', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Trim', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.1 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'weapon_rack', name: 'Weapon Rack', kind: 'item', complexity: 2,
    slots: [
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 2, weights: { DEN: 0.4 } },
      { name: 'Cradles', accepts: 'component:fiber_weave', qty: 2, weights: { FLX: 0.3 } },
      { name: 'Backlight', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.2 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'hover_chair', name: 'Hover Chair', kind: 'item', complexity: 3,
    slots: [
      { name: 'Shell', accepts: 'component:metal_ingot', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Cushion', accepts: 'component:fiber_weave', qty: 2, weights: { FLX: 0.5 } },
      { name: 'Lift core', accepts: 'component:servo_motor', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'orchestrion', name: 'Orchestrion', kind: 'item', complexity: 3,
    slots: [
      { name: 'Cabinet', accepts: 'component:timber_plank', qty: 3, weights: { DEN: 0.3 } },
      { name: 'Horn', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Works', accepts: 'component:servo_motor', qty: 1, weights: { PUR: 0.3 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'planter_hydro', name: 'Hydroponic Planter', kind: 'item', complexity: 2,
    slots: [
      { name: 'Trough', accepts: 'component:metal_ingot', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Grow light', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Piping', accepts: 'component:insulated_wire', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'mirror_tall', name: 'Tall Mirror', kind: 'item', complexity: 2,
    slots: [
      { name: 'Glass', accepts: 'component:glass_pane', qty: 2, weights: { LUS: 0.5 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.2 } },
    ],
    props: ['radiance'],
  },
  {
    key: 'guestbook_pedestal', name: 'Guest Book', kind: 'item', complexity: 2,
    slots: [
      { name: 'Lectern', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Holo book', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Trim', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['artistry'],
  },
  // ── THE INTERIOR ITEMS (2026-10-01, the interiors programme): the counters, desks, racks and cases of the rooms
  // that are not homes. Complexity 2–3 over existing components — none is gated (basic work is open to everyone). ──
  {
    key: 'counter_segment', name: 'Counter Segment', kind: 'item', complexity: 2,
    slots: [
      { name: 'Worktop', accepts: 'component:timber_plank', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Carcass', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Foot rail', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.3 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'reception_desk', name: 'Reception Desk', kind: 'item', complexity: 3,
    slots: [
      { name: 'Desk', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Ledge', accepts: 'component:timber_plank', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Status light', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.35 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'service_trolley', name: 'Service Trolley', kind: 'item', complexity: 2,
    slots: [
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 2, weights: { DEN: 0.4 } },
      { name: 'Trays', accepts: 'component:alloy_plate', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Linen', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.3 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'notice_case', name: 'Notice Board', kind: 'item', complexity: 2,
    slots: [
      { name: 'Board', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Strip light', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'privacy_screen', name: 'Privacy Screen', kind: 'item', complexity: 2,
    slots: [
      { name: 'Panels', accepts: 'component:fiber_weave', qty: 3, weights: { FLX: 0.6 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.4 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'seat_row', name: 'Seat Row', kind: 'item', complexity: 2,
    slots: [
      { name: 'Shells', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Cushions', accepts: 'component:fiber_weave', qty: 3, weights: { FLX: 0.5 } },
      { name: 'Rail', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['comfort'],
  },
  {
    key: 'display_plinth', name: 'Display Plinth', kind: 'item', complexity: 2,
    slots: [
      { name: 'Drum', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Base', accepts: 'component:metal_ingot', qty: 1, weights: { PUR: 0.3 } },
      { name: 'Light ring', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'exhibit_case', name: 'Exhibit Case', kind: 'item', complexity: 3,
    slots: [
      { name: 'Glazing', accepts: 'component:glass_pane', qty: 3, weights: { LUS: 0.5 } },
      { name: 'Base', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Strip light', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.2 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'council_table', name: 'Council Table', kind: 'item', complexity: 3,
    slots: [
      { name: 'Top', accepts: 'component:timber_plank', qty: 3, weights: { PUR: 0.3 } },
      { name: 'Trestles', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Map inset', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.4 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'parts_rack', name: 'Parts Rack', kind: 'item', complexity: 2,
    slots: [
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 3, weights: { DEN: 0.5 } },
      { name: 'Shelves', accepts: 'component:timber_plank', qty: 2, weights: { PUR: 0.3 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'locker_bank', name: 'Locker Bank', kind: 'item', complexity: 2,
    slots: [
      { name: 'Doors', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.45 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 2, weights: { PUR: 0.35 } },
      { name: 'Latches', accepts: 'component:insulated_wire', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'wash_unit', name: 'Wash Unit', kind: 'item', complexity: 3,
    slots: [
      { name: 'Counter', accepts: 'family:stone', qty: 12, weights: { DEN: 0.3 } },
      { name: 'Cupboards', accepts: 'component:alloy_plate', qty: 1, weights: { PUR: 0.35 } },
      { name: 'Plumbing', accepts: 'component:sealant_gel', qty: 1, weights: { FLX: 0.35 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'vault_door', name: 'Vault Door', kind: 'item', complexity: 3,
    slots: [
      { name: 'Armour', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.45 } },
      { name: 'Plate', accepts: 'component:alloy_plate', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Lockwork', accepts: 'component:servo_motor', qty: 1, weights: { LUS: 0.25 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'wall_sconce', name: 'Wall Sconce', kind: 'item', complexity: 2,
    slots: [
      { name: 'Shade', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.4 } },
      { name: 'Back-plate', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.2 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.4 } },
    ],
    props: ['radiance'],
  },
  // ── ROUND 2 (2026-10-01): the bar table, the back-bar and retail shelves, the retail island, the process machine,
  // the stage curtain, the lectern and the altar. Complexity 2–3 over existing components — none is gated. ──
  {
    key: 'cantina_table', name: 'Cantina Table', kind: 'item', complexity: 2,
    slots: [
      { name: 'Top', accepts: 'component:timber_plank', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Column', accepts: 'component:metal_ingot', qty: 2, weights: { DEN: 0.4 } },
      { name: 'Light ring', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'service_shelf', name: 'Back-Bar Shelf', kind: 'item', complexity: 3,
    slots: [
      { name: 'Shelves', accepts: 'component:timber_plank', qty: 3, weights: { PUR: 0.3 } },
      { name: 'Carcass', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Strip lights', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.35 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'display_shelf', name: 'Display Shelf', kind: 'item', complexity: 3,
    slots: [
      { name: 'Shelves', accepts: 'component:timber_plank', qty: 3, weights: { PUR: 0.35 } },
      { name: 'Frame', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Header light', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.35 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'display_table', name: 'Display Table', kind: 'item', complexity: 2,
    slots: [
      { name: 'Top', accepts: 'component:timber_plank', qty: 3, weights: { PUR: 0.4 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 2, weights: { DEN: 0.4 } },
      { name: 'Trim', accepts: 'component:alloy_plate', qty: 1, weights: { LUS: 0.2 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'process_machine', name: 'Process Machine', kind: 'item', complexity: 3,
    slots: [
      { name: 'Housing', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Process window', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.2 } },
      { name: 'Drive', accepts: 'component:servo_motor', qty: 1, weights: { VOL: 0.4 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'stage_curtain', name: 'Stage Curtain', kind: 'item', complexity: 2,
    slots: [
      { name: 'Drape', accepts: 'component:fiber_weave', qty: 4, weights: { FLX: 0.6 } },
      { name: 'Valance', accepts: 'component:timber_plank', qty: 1, weights: { PUR: 0.2 } },
      { name: 'Rail', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.2 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'lectern', name: 'Lectern', kind: 'item', complexity: 2,
    slots: [
      { name: 'Body', accepts: 'component:timber_plank', qty: 3, weights: { PUR: 0.5 } },
      { name: 'Rest', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Trim', accepts: 'component:metal_ingot', qty: 1, weights: { LUS: 0.2 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'altar', name: 'Altar', kind: 'item', complexity: 3,
    slots: [
      { name: 'Stone', accepts: 'family:stone', qty: 20, weights: { DEN: 0.5 } },
      { name: 'Niche light', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.3 } },
      { name: 'Ember cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.2 } },
    ],
    props: ['artistry'],
  },
  {
    key: 'cooler_unit', name: 'Cold Cooler', kind: 'item', complexity: 3,
    slots: [
      { name: 'Shell', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Seal', accepts: 'component:cured_hide', qty: 1, weights: { RES: 0.25 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.4 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'bench_work', name: 'Home Workbench', kind: 'item', complexity: 2,
    slots: [
      { name: 'Top', accepts: 'component:timber_plank', qty: 2, weights: { DEN: 0.45 } },
      { name: 'Frame', accepts: 'component:metal_ingot', qty: 2, weights: { PUR: 0.35 } },
      { name: 'Tools', accepts: 'family:ferrite_ore', qty: 8, weights: { RSN: 0.2 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'desk_terminal', name: 'Console Desk', kind: 'item', complexity: 3,
    slots: [
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Desk', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.3 } },
    ],
    props: ['sturdiness'],
  },
  {
    key: 'fabricator_pod', name: 'Home Fabricator', kind: 'item', complexity: 3,
    slots: [
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.35 } },
      { name: 'Alcove', accepts: 'component:glass_pane', qty: 1, weights: { LUS: 0.2 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.25 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.2 } },
    ],
    props: ['sturdiness'],
  },
  // ── hovercraft (the vehicle wave): a grav coil feeds five tiered skimmers.
  //    Cost law holds: higher tiers take deeper trees, rarer families
  //    (noble ore → storm core) and bigger quantities. All ≥3 complexity —
  //    a vehicle is bench-built at a workshop, never in a field kit.
  {
    key: 'grav_coil', name: 'Grav Coil', kind: 'component', complexity: 3,
    slots: [
      { name: 'Noble Ore', accepts: 'family:noble_ore', qty: 10, weights: { PUR: 0.4, LUS: 0.2 } },
      { name: 'Crystal', accepts: 'family:crystal', qty: 8, weights: { RSN: 0.3 } },
      { name: 'Reagent', accepts: 'family:reagent', qty: 6, weights: { VOL: 0.1 } },
    ],
    props: ['lift'],
  },
  {
    key: 'hover_skiff', name: 'Dust Skiff', kind: 'item', complexity: 3,
    slots: [
      { name: 'Coil', accepts: 'component:grav_coil', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Seat', accepts: 'component:cured_hide', qty: 1, weights: { FLX: 0.25 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_dune', name: 'Dune Runner', kind: 'item', complexity: 3,
    slots: [
      { name: 'Coil', accepts: 'component:grav_coil', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Skirt', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.3 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_falcon', name: 'Sky Falcon', kind: 'item', complexity: 4,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 2, weights: { RSN: 0.4 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.25 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.35 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_raptor', name: 'Ridge Raptor', kind: 'item', complexity: 4,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 2, weights: { RSN: 0.35 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 3, weights: { DEN: 0.25 } },
      { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.4 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_tempest', name: 'Storm Tempest', kind: 'item', complexity: 5,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 3, weights: { RSN: 0.3 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 3, weights: { DEN: 0.2 } },
      { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.2 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { VOL: 0.3 } },
    ],
    props: ['thrust'],
  },
  // ── The 2026-10-08 wave: ten more skimmers, two per tier. The cost law holds by TIER GROUP: no craft of a higher tier
  //    takes a shallower or simpler tree than any craft of a lower one (verify:vehicles); tier 3 and up are gated. ──
  {
    key: 'hover_mule', name: 'Pack Mule', kind: 'item', complexity: 3,
    slots: [
      { name: 'Coil', accepts: 'component:grav_coil', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.35 } },
      { name: 'Tarp', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.25 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_punt', name: 'Reed Punt', kind: 'item', complexity: 3,
    slots: [
      { name: 'Coil', accepts: 'component:grav_coil', qty: 1, weights: { RSN: 0.4 } },
      { name: 'Planks', accepts: 'component:timber_plank', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Seat', accepts: 'component:cured_hide', qty: 1, weights: { FLX: 0.3 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_strider', name: 'Fen Strider', kind: 'item', complexity: 3,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 2, weights: { RSN: 0.45 } },
      { name: 'Floats', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.3 } },
      { name: 'Deck', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.25 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_lark', name: 'Road Lark', kind: 'item', complexity: 3,
    slots: [
      { name: 'Coil', accepts: 'component:grav_coil', qty: 1, weights: { RSN: 0.35 } },
      { name: 'Body', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.35 } },
      { name: 'Screen', accepts: 'component:glass_pane', qty: 1, weights: { PUR: 0.3 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_wasp', name: 'Canyon Wasp', kind: 'item', complexity: 4,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 2, weights: { RSN: 0.35 } },
      { name: 'Booms', accepts: 'component:alloy_plate', qty: 1, weights: { DEN: 0.2 } },
      { name: 'Engines', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.45 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_ox', name: 'Iron Ox', kind: 'item', complexity: 4,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 2, weights: { RSN: 0.3 } },
      { name: 'Plating', accepts: 'component:alloy_plate', qty: 3, weights: { DEN: 0.4 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.3 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_needle', name: 'Glass Needle', kind: 'item', complexity: 4,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 2, weights: { RSN: 0.3 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.15 } },
      { name: 'Nozzle', accepts: 'component:thruster_nozzle', qty: 1, weights: { VOL: 0.25 } },
      { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.15 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.15 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_marshal', name: 'Trail Marshal', kind: 'item', complexity: 4,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 2, weights: { RSN: 0.3 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 3, weights: { DEN: 0.25 } },
      { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.2 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.25 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_courser', name: 'Gilt Courser', kind: 'item', complexity: 5,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 3, weights: { RSN: 0.3 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 4, weights: { DEN: 0.25 } },
      { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.15 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { VOL: 0.3 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'hover_kite', name: 'Winter Kite', kind: 'item', complexity: 5,
    slots: [
      { name: 'Coils', accepts: 'component:grav_coil', qty: 3, weights: { RSN: 0.3 } },
      { name: 'Hull', accepts: 'component:alloy_plate', qty: 3, weights: { DEN: 0.15 } },
      { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.15 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 1, weights: { VOL: 0.1 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { VOL: 0.3 } },
    ],
    props: ['thrust'],
  },
  // ── extraction rigs (deed outputs — craftItem mints a structureDeed, the
  //    SWG harvester lineage: manufacture the deed, place it on your lots,
  //    keep it fed; crafted quality arrives as storedMaint head start).
  {
    key: 'extractor_small', name: 'Field Extractor Rig (deed)', kind: 'item', complexity: 3,
    slots: [
      { name: 'Frame', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.35 } },
      { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.35 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.3 } },
    ],
    props: ['yield'],
  },
  {
    key: 'extractor_medium', name: 'Industrial Extractor Rig (deed)', kind: 'item', complexity: 4,
    slots: [
      { name: 'Foundation', accepts: 'component:stone_block', qty: 1, weights: { DEN: 0.1 } },
      { name: 'Frame', accepts: 'component:alloy_plate', qty: 3, weights: { DEN: 0.2 } },
      { name: 'Brains', accepts: 'component:circuit_lattice', qty: 2, weights: { PUR: 0.35 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 2, weights: { VOL: 0.35 } },
    ],
    props: ['yield'],
  },
  {
    key: 'extractor_large', name: 'Heavy Extractor Rig (deed)', kind: 'item', complexity: 5,
    slots: [
      { name: 'Foundation', accepts: 'component:stone_block', qty: 2, weights: { DEN: 0.1 } },
      { name: 'Frame', accepts: 'component:alloy_plate', qty: 4, weights: { DEN: 0.15 } },
      { name: 'Brains', accepts: 'component:circuit_lattice', qty: 2, weights: { PUR: 0.3 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 2, weights: { VOL: 0.25 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { RSN: 0.2 } },
    ],
    props: ['yield'],
  },

  // ── SCHEMATIC WAVE (2026-09-10): more raw → component BASES so every
  //    finished good climbs from ore, gas, stone, resin and fiber, plus the
  //    ship-building tier (segments, nozzles, nav cores, servos, coolant).
  //    Every key here needs an inventory icon — gen-schematic-icons.mjs fills
  //    the missing ones from the catalog itself. ──
  {
    key: 'stone_block', name: 'Stone Block', kind: 'component', complexity: 1,
    slots: [
      { name: 'Stone', accepts: 'family:stone', qty: 16, weights: { DEN: 0.6, RES: 0.3 } },
      { name: 'Binder', accepts: 'family:solvent', qty: 4, weights: { PUR: 0.1 } },
    ],
    props: ['mass'],
  },
  {
    key: 'sealant_gel', name: 'Sealant Gel', kind: 'component', complexity: 1,
    slots: [
      { name: 'Resin', accepts: 'family:resin', qty: 12, weights: { FLX: 0.5, VOL: 0.2 } },
      { name: 'Solvent', accepts: 'family:solvent', qty: 6, weights: { PUR: 0.3 } },
    ],
    props: ['seal'],
  },
  {
    key: 'cryo_coolant', name: 'Cryo Coolant', kind: 'component', complexity: 2,
    slots: [
      { name: 'Gas', accepts: 'family:gas', qty: 12, weights: { VOL: 0.5, RSN: 0.2 } },
      { name: 'Solvent', accepts: 'family:solvent', qty: 8, weights: { PUR: 0.3 } },
    ],
    props: ['chill'],
  },
  {
    key: 'insulated_wire', name: 'Insulated Wire', kind: 'component', complexity: 2,
    slots: [
      { name: 'Noble Ore', accepts: 'family:noble_ore', qty: 8, weights: { PUR: 0.5, LUS: 0.2 } },
      { name: 'Fiber', accepts: 'family:fiber', qty: 8, weights: { FLX: 0.3 } },
    ],
    props: ['conductance'],
  },
  {
    key: 'ceramic_tile', name: 'Ceramic Tile', kind: 'component', complexity: 2,
    slots: [
      { name: 'Stone', accepts: 'family:stone', qty: 12, weights: { DEN: 0.4, RES: 0.3 } },
      { name: 'Gas', accepts: 'family:gas', qty: 6, weights: { VOL: 0.3 } },
    ],
    props: ['heat_shield'],
  },
  {
    key: 'thruster_nozzle', name: 'Thruster Nozzle', kind: 'component', complexity: 2,
    slots: [
      { name: 'Ore', accepts: 'family:ferrite_ore', qty: 22, weights: { DEN: 0.5, PUR: 0.2 } },
      { name: 'Gas', accepts: 'family:gas', qty: 10, weights: { VOL: 0.3 } },
    ],
    props: ['thrust'],
  },
  {
    key: 'preserved_rations', name: 'Preserved Rations', kind: 'component', complexity: 1,
    slots: [
      { name: 'Meat', accepts: 'family:game', qty: 10, weights: { NUT: 0.5, RES: 0.2 } },
      { name: 'Salt', accepts: 'family:reagent', qty: 4, weights: { PUR: 0.3 } },
    ],
    props: ['keeping'],
  },
  {
    key: 'servo_motor', name: 'Servo Motor', kind: 'component', complexity: 2,
    slots: [
      { name: 'Ingot', accepts: 'component:metal_ingot', qty: 1, weights: { DEN: 0.4 } },
      { name: 'Wire', accepts: 'component:insulated_wire', qty: 1, weights: { PUR: 0.4 } },
      { name: 'Resin', accepts: 'family:resin', qty: 4, weights: { FLX: 0.2 } },
    ],
    props: ['torque'],
  },
  {
    key: 'nav_core', name: 'Nav Core', kind: 'component', complexity: 3,
    slots: [
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { RSN: 0.5 } },
      { name: 'Crystal', accepts: 'family:crystal', qty: 6, weights: { LUS: 0.3, PUR: 0.2 } },
    ],
    props: ['guidance'],
  },
  {
    key: 'hull_segment', name: 'Hull Segment', kind: 'component', complexity: 3,
    slots: [
      { name: 'Plates', accepts: 'component:alloy_plate', qty: 2, weights: { DEN: 0.5 } },
      { name: 'Tiles', accepts: 'component:ceramic_tile', qty: 1, weights: { RES: 0.3 } },
      { name: 'Seal', accepts: 'component:sealant_gel', qty: 1, weights: { FLX: 0.2 } },
    ],
    props: ['integrity'],
  },
  {
    key: 'survival_kit', name: 'Survival Kit', kind: 'item', complexity: 2,
    slots: [
      { name: 'Rations', accepts: 'component:preserved_rations', qty: 2, weights: { NUT: 0.6 } },
      { name: 'Wrap', accepts: 'component:fiber_weave', qty: 1, weights: { FLX: 0.2 } },
      { name: 'Salts', accepts: 'family:reagent', qty: 4, weights: { PUR: 0.2 } },
    ],
    props: ['sustenance'],
  },
  // Space campaign slice G (2026-09-30): the ship REPAIR KIT — the field patch's one consumable (report §7.3: 2 ingots +
  // 1 tool → 1 kit). One kit = one 8-second patch: ≤ 20 to a subsystem group (never past 60) or ≤ 10 hull HP.
  {
    key: 'repair_kit', name: 'Repair Kit', kind: 'item', complexity: 2,
    slots: [
      { name: 'Ingots', accepts: 'component:metal_ingot', qty: 2, weights: { DEN: 0.4, PUR: 0.3 } },
      { name: 'Tool', accepts: 'component:field_tool', qty: 1, weights: { FLX: 0.3 } },
    ],
    props: ['restoration'],
  },
  // ── SHIP HULLS: every chassis of the fleet is CRAFTABLE — the craft mints a
  //    playerShip into the hangar (like a rig schematic mints a deed). All
  //    schematic-GATED (requiresSchematic): learn the design from a schematic
  //    dealer in a city or town, or reverse-engineer a hull you own. The cost
  //    law holds — heavier hulls take more segments, nozzles and cores. ──
  {
    key: 'ship_lancer', name: 'Lancer Hull', kind: 'item', complexity: 5,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 2, weights: { DEN: 0.4 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.2 } },
      { name: 'Servo', accepts: 'component:servo_motor', qty: 1, weights: { PUR: 0.2 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_talon', name: 'Talon Hull', kind: 'item', complexity: 6,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 2, weights: { DEN: 0.3 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.25 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.15 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 1, weights: { VOL: 0.15 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_courier', name: 'Courier Hull', kind: 'item', complexity: 6,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 3, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.15 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.15 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_bulwark', name: 'Bulwark Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 4, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.15 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 2, weights: { VOL: 0.1 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.15 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_sovereign', name: 'Sovereign Hull', kind: 'item', complexity: 8,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 6, weights: { DEN: 0.3 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.15 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 2, weights: { VOL: 0.1 } },
      { name: 'Frames', accepts: 'component:reinforced_frame', qty: 2, weights: { DEN: 0.1 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  // ── The 2026-10-08 wave: twenty more hulls (docs/research/2026-10-08-ships-vehicles/PLAN.md). The part counts keep
  //    the ladder above — 6 / 9 / 13 / 20 parts at complexity 5 / 6 / 7 / 8 — and every one is schematic-GATED. ──
  {
    key: 'ship_wren', name: 'Wren Hull', kind: 'item', complexity: 5,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 1, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.25 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.2 } },
      { name: 'Servo', accepts: 'component:servo_motor', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_packet', name: 'Packet Hull', kind: 'item', complexity: 5,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 2, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.25 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.2 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_pathfinder', name: 'Pathfinder Hull', kind: 'item', complexity: 6,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 2, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_kestrel', name: 'Kestrel Hull', kind: 'item', complexity: 6,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 1, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 1, weights: { VOL: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_warden', name: 'Warden Hull', kind: 'item', complexity: 6,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 3, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servo', accepts: 'component:servo_motor', qty: 1, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 1, weights: { VOL: 0.1 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_prospector', name: 'Prospector Hull', kind: 'item', complexity: 6,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 2, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_gleaner', name: 'Gleaner Hull', kind: 'item', complexity: 6,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 3, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 2, weights: { VOL: 0.25 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.2 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_harrier', name: 'Harrier Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 3, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 2, weights: { VOL: 0.1 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_vesper', name: 'Vesper Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 2, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 2, weights: { VOL: 0.1 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_farstrider', name: 'Farstrider Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 3, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 1, weights: { VOL: 0.1 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_arbalest', name: 'Arbalest Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 4, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.1 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_drover', name: 'Drover Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 5, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Frames', accepts: 'component:reinforced_frame', qty: 2, weights: { DEN: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_tender', name: 'Tender Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 5, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 1, weights: { VOL: 0.1 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_delver', name: 'Delver Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 5, weights: { DEN: 0.45 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
      { name: 'Cell', accepts: 'component:power_cell', qty: 1, weights: { VOL: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_laurel', name: 'Laurel Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 4, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 1, weights: { VOL: 0.1 } },
      { name: 'Frame', accepts: 'component:reinforced_frame', qty: 1, weights: { DEN: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_rime', name: 'Rime Hull', kind: 'item', complexity: 7,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 3, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 2, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 2, weights: { VOL: 0.1 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_ironclad', name: 'Ironclad Hull', kind: 'item', complexity: 8,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 7, weights: { DEN: 0.25 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 2, weights: { VOL: 0.1 } },
      { name: 'Frames', accepts: 'component:reinforced_frame', qty: 2, weights: { DEN: 0.1 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_bastion', name: 'Bastion Hull', kind: 'item', complexity: 8,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 5, weights: { DEN: 0.25 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 3, weights: { VOL: 0.15 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 2, weights: { RSN: 0.1 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
      { name: 'Coolant', accepts: 'component:cryo_coolant', qty: 3, weights: { VOL: 0.1 } },
      { name: 'Cells', accepts: 'component:power_cell', qty: 2, weights: { VOL: 0.1 } },
      { name: 'Lattice', accepts: 'component:circuit_lattice', qty: 1, weights: { PUR: 0.1 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_argosy', name: 'Argosy Hull', kind: 'item', complexity: 8,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 8, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 3, weights: { PUR: 0.1 } },
      { name: 'Frames', accepts: 'component:reinforced_frame', qty: 3, weights: { DEN: 0.1 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
  {
    key: 'ship_reclaimer', name: 'Reclaimer Hull', kind: 'item', complexity: 8,
    slots: [
      { name: 'Hull', accepts: 'component:hull_segment', qty: 7, weights: { DEN: 0.35 } },
      { name: 'Nozzles', accepts: 'component:thruster_nozzle', qty: 4, weights: { VOL: 0.2 } },
      { name: 'Nav', accepts: 'component:nav_core', qty: 1, weights: { RSN: 0.15 } },
      { name: 'Servos', accepts: 'component:servo_motor', qty: 4, weights: { PUR: 0.1 } },
      { name: 'Frames', accepts: 'component:reinforced_frame', qty: 3, weights: { DEN: 0.1 } },
      { name: 'Core', accepts: 'component:storm_core', qty: 1, weights: { PUR: 0.1 } },
    ],
    props: ['airframe'],
  },
];


export const SCHEMATIC_BY_KEY: Record<string, Schematic> = Object.fromEntries(SCHEMATICS.map((s) => [s.key, s]));

/** Schematics whose craft OUTPUT is a placeable structure DEED, not an item
 *  (the SWG harvester lineage) — craftItem consults this map. */
export const DEED_SCHEMATICS: Record<string, string> = {
  extractor_small: 'extractor_small',
  extractor_medium: 'extractor_medium',
  extractor_large: 'extractor_large',
  // THE SWG SET (2026-09-23): every player-city building is a craftable deed (deed_<type> → <type>).
  deed_small_house: 'small_house', deed_medium_house: 'medium_house', deed_large_house: 'large_house', deed_xlarge_house: 'xlarge_house',
  deed_city_hall: 'city_hall', deed_cantina: 'cantina', deed_museum: 'museum', deed_bunker: 'bunker', deed_outpost: 'outpost',
  deed_turret: 'turret', deed_city_wall: 'city_wall', deed_store: 'store', deed_market: 'market', deed_garden: 'garden',
  deed_statue: 'statue', deed_fountain: 'fountain', deed_street_lamp: 'street_lamp', deed_garage: 'garage', deed_pool: 'pool',
  deed_city_console: 'city_console', deed_hospital: 'hospital', deed_shuttleport: 'shuttleport', deed_spaceport: 'spaceport',
  deed_hotel: 'hotel', deed_salon: 'salon', deed_factory: 'factory', deed_mining_facility: 'mining_facility',
  deed_bank: 'bank', deed_theater: 'theater', deed_cloning: 'cloning', deed_guild_hall: 'guild_hall',
};

/** The deed recipes of the SWG set, appended to SCHEMATICS below (one per DEED_SCHEMATICS entry above
 *  beyond the three rigs). Houses and decor are BASIC work (complexity ≤ 3); civic, industrial and the
 *  ports are complex designs under the SCHEMATIC LAW (≥ 4: a dealer's board or reverse engineering). */
function deedRecipe(key: string, name: string, complexity: number, plates: number, extra: { accepts: string; qty: number; name: string }[]): Schematic {
  return {
    key, name: `${name} (deed)`, kind: 'item', complexity,
    slots: [
      { name: 'Frame', accepts: 'component:alloy_plate', qty: plates, weights: { DEN: 0.4 } },
      { name: 'Foundation', accepts: 'component:stone_block', qty: Math.max(1, Math.round(plates / 2)), weights: { DEN: 0.2 } },
      ...extra.map((e) => ({ name: e.name, accepts: e.accepts, qty: e.qty, weights: { PUR: 0.4 / Math.max(1, extra.length) } })),
    ],
    props: ['durability'],
  };
}
const PANE = { name: 'Panes', accepts: 'component:ceramic_tile', qty: 2 };
const WIRE = { name: 'Wiring', accepts: 'component:insulated_wire', qty: 2 };
const BRAIN = { name: 'Brain', accepts: 'component:circuit_lattice', qty: 1 };
const CELL = { name: 'Cell', accepts: 'component:power_cell', qty: 1 };
const SERVO = { name: 'Servo', accepts: 'component:servo_motor', qty: 1 };
const SEAL = { name: 'Sealant', accepts: 'component:sealant_gel', qty: 1 };
export const DEED_SET_SCHEMATICS: Schematic[] = [
  deedRecipe('deed_small_house', 'Small House', 2, 3, [PANE]),
  deedRecipe('deed_medium_house', 'Medium House', 2, 4, [PANE, WIRE]),
  deedRecipe('deed_large_house', 'Large House', 3, 6, [PANE, WIRE]),
  deedRecipe('deed_xlarge_house', 'Manor', 3, 8, [PANE, WIRE, SEAL]),
  deedRecipe('deed_garden', 'Garden', 1, 1, [SEAL]),
  deedRecipe('deed_statue', 'Statue', 2, 2, []),
  deedRecipe('deed_fountain', 'Fountain', 2, 2, [SEAL]),
  deedRecipe('deed_street_lamp', 'Street Lamp', 1, 1, [WIRE]),
  deedRecipe('deed_city_console', 'City Console', 3, 1, [BRAIN, WIRE]),
  deedRecipe('deed_store', 'General Store', 3, 4, [PANE, WIRE]),
  deedRecipe('deed_salon', 'Salon', 3, 3, [PANE, WIRE]),
  deedRecipe('deed_outpost', 'Outpost', 3, 3, [WIRE]),
  deedRecipe('deed_city_wall', 'City Wall', 2, 3, []),
  deedRecipe('deed_city_hall', 'City Hall', 4, 10, [PANE, WIRE, BRAIN]),
  deedRecipe('deed_cantina', 'Entertainment Center', 4, 6, [PANE, WIRE, CELL]),
  deedRecipe('deed_museum', 'Museum', 4, 6, [PANE, WIRE]),
  deedRecipe('deed_bunker', 'Bunker', 4, 6, [SEAL, CELL]),
  deedRecipe('deed_turret', 'Defense Turret', 5, 4, [BRAIN, CELL, SERVO]),
  deedRecipe('deed_market', 'Market', 4, 5, [PANE, WIRE]),
  deedRecipe('deed_garage', 'Vehicle Garage', 4, 5, [WIRE, SERVO]),
  deedRecipe('deed_pool', 'Pool', 4, 4, [SEAL, WIRE]),
  deedRecipe('deed_hospital', 'Medical Center', 5, 6, [PANE, WIRE, BRAIN]),
  deedRecipe('deed_hotel', 'Hotel', 4, 8, [PANE, WIRE]),
  deedRecipe('deed_factory', 'Factory', 5, 8, [WIRE, SERVO, CELL]),
  deedRecipe('deed_mining_facility', 'Mining Facility', 5, 6, [SERVO, CELL]),
  deedRecipe('deed_shuttleport', 'Shuttle Port', 5, 8, [WIRE, BRAIN, CELL]),
  deedRecipe('deed_spaceport', 'Space Port', 6, 14, [WIRE, BRAIN, CELL, SERVO]),
  deedRecipe('deed_bank', 'Bank', 4, 5, [BRAIN, WIRE]),
  deedRecipe('deed_theater', 'Theater', 4, 6, [PANE, WIRE, CELL]),
  deedRecipe('deed_cloning', 'Cloning Facility', 5, 6, [BRAIN, CELL, SEAL]),
  deedRecipe('deed_guild_hall', 'Guild Hall', 4, 8, [PANE, WIRE]),
];
// The deed set joins the catalog (SCHEMATIC_BY_KEY was built above; GATED_SCHEMATICS filters below).
for (const d of DEED_SET_SCHEMATICS) { SCHEMATICS.push(d); SCHEMATIC_BY_KEY[d.key] = d; }

/** Schematics whose craft OUTPUT is a SHIP in the hangar (a playerShip row),
 *  not an inventory item — key → chassisKey (server FLEET_CHASSIS / client
 *  CHASSIS). craftItem + debugGrantItem consult this map. */
export const SHIP_SCHEMATICS: Record<string, string> = {
  ship_lancer: 'ship_lancer',
  ship_talon: 'ship_talon',
  ship_courier: 'ship_courier',
  ship_bulwark: 'ship_bulwark',
  ship_sovereign: 'ship_sovereign',
  ship_wren: 'ship_wren',
  ship_packet: 'ship_packet',
  ship_pathfinder: 'ship_pathfinder',
  ship_kestrel: 'ship_kestrel',
  ship_warden: 'ship_warden',
  ship_prospector: 'ship_prospector',
  ship_gleaner: 'ship_gleaner',
  ship_harrier: 'ship_harrier',
  ship_vesper: 'ship_vesper',
  ship_farstrider: 'ship_farstrider',
  ship_arbalest: 'ship_arbalest',
  ship_drover: 'ship_drover',
  ship_tender: 'ship_tender',
  ship_delver: 'ship_delver',
  ship_laurel: 'ship_laurel',
  ship_rime: 'ship_rime',
  ship_ironclad: 'ship_ironclad',
  ship_bastion: 'ship_bastion',
  ship_argosy: 'ship_argosy',
  ship_reclaimer: 'ship_reclaimer',
};

/** The SCHEMATIC LAW (2026-09-10): BASIC work is open to everyone — every
 *  component and every finished item up to complexity 3. COMPLEX items
 *  (complexity ≥ 4: rig cores, ship components, droids, tier-3+ weapons,
 *  the faster skimmers, industrial rigs) and every ship hull need the
 *  DESIGN in hand: a player_schematic row, bought from a schematic dealer
 *  in a city or town, or learned by reverse-engineering a finished piece
 *  (which destroys it). */
export const SCHEMATIC_GATE_COMPLEXITY = 4;

export function requiresSchematic(sch: Schematic): boolean {
  if (sch.kind !== 'item') return false;
  if (sch.key in SHIP_SCHEMATICS) return true;
  return sch.complexity >= SCHEMATIC_GATE_COMPLEXITY;
}

/** Dealer asking price by complexity (SOV) — jittered ±15% per offer. */
export const SCHEMATIC_PRICE_SOV: Record<number, number> = {
  4: 350, 5: 800, 6: 1600, 7: 3200, 8: 6000,
};

export function schematicPriceSov(sch: Schematic): number {
  return SCHEMATIC_PRICE_SOV[Math.min(8, Math.max(4, sch.complexity))] ?? 800;
}

/** Every schematic that is gated — the dealer draw pool. */
export const GATED_SCHEMATICS: Schematic[] = SCHEMATICS.filter(requiresSchematic);

export type SchematicCategory =
  | 'component' | 'tool' | 'consumable' | 'weapon' | 'vehicle' | 'ship'
  | 'ship component' | 'droid' | 'furniture' | 'rig';

/** The guide's grouping — derived from key prefixes + the deed/ship maps, so
 *  a new schematic lands in a group without a second table. */
export function schematicCategory(sch: Schematic): SchematicCategory {
  if (sch.kind === 'component') return 'component';
  if (sch.key in SHIP_SCHEMATICS) return 'ship';
  if (sch.key in DEED_SCHEMATICS) return 'rig';
  if (sch.key.startsWith('comp_')) return 'ship component';
  if (sch.key.startsWith('droid_')) return 'droid';
  if (sch.key.startsWith('hover_')) return 'vehicle';
  if (sch.props.includes('damage')) return 'weapon';
  if (sch.props.some((p) => ['comfort', 'sturdiness', 'radiance', 'artistry'].includes(p))) return 'furniture';
  if (['trail_rations', 'medic_salve', 'signal_beacon', 'survival_kit', 'repair_kit'].includes(sch.key)) return 'consumable';
  return 'tool';
}

